/**
 * Narrow a doctype catalog to the ONE eCTD leaf a document is.
 *
 * Shared by BOTH surfaces — the Word add-in and the web app. It lives here,
 * not in either app, because the rule it implements is the RENDERER's rule:
 * `word_writer.build` walks `[entry for entry in skeleton if entry[0] in
 * sections_of_document(config, document_id)]`, and this is that same filter
 * over the same partition. Two copies of it — one per surface — is how a
 * surface's outline and the produced .docx start disagreeing about what a leaf
 * contains, and that disagreement is invisible until a submission is assembled.
 *
 * The partition itself is resolved SERVER-side by
 * `src/csr_sim/doc_types/documents.py::effective_documents` and arrives on
 * `catalog.documents`. Nothing here re-derives it.
 *
 * Pure: no React, no DOM, no I/O — so both surfaces can test it without a
 * harness.
 */

import { moduleDocumentMap } from "./documentPicker";

/** The subset of `DocTypeModules` (shared/client/runs.ts) the outline reads. */
export interface OutlineCatalog {
  /** Optional + readonly-tolerant, for the same reason `DocumentPickerCatalog`
   *  is: both surfaces' own doctype types must satisfy this structurally
   *  without an adapter at the call site. */
  modules?: Readonly<Record<string, { readonly title?: string; readonly sections?: readonly string[] }>>;
  skeleton?: readonly (readonly [string, string, number])[];
  gates?: readonly { readonly name: string; readonly display_name?: string; readonly modules?: readonly string[] }[];
  /** The eCTD-leaf partition, in submission order. `null`/absent for the 14
   *  doctypes that are legitimately one document. */
  documents?: readonly { readonly id: string; readonly title: string; readonly sections: readonly string[] }[] | null;
  /** Leaves the doctype USED to declare (`legacy_documents:` in the manifest).
   *  Resolvable for a document that already stored one of these ids, never
   *  offered by the picker. Mirrors `documents.py::find_document`. */
  legacy_documents?: readonly { readonly id: string; readonly title: string; readonly sections: readonly string[] }[] | null;
  /** WHICH module is this doctype's title page — resolved server-side by
   *  `csr_sim.doc_types.documents.title_module` (i.e. by `is_title_module`,
   *  which reconciles the three conventions doctypes declare a title module
   *  by). `null`/absent for a doctype that declares none. Never matched
   *  against a `meta/title-page` / `"TITLE"` literal here: the moment the
   *  client re-derives this, a new doctype grows a title page the outline no
   *  longer recognises. */
  title_module_id?: string | null;
  /** Set by `scopeCatalogToLeaf` (CLIENT-side, never served) when — and only
   *  when — the narrowing actually applied: this catalog IS one eCTD leaf.
   *  It is what lets `buildOutlineRows` answer "does this document have a
   *  title page?" on a catalog that was already scoped by its caller, without
   *  the caller having to remember to say so a second time. */
  leafDocumentId?: string | null;
}

/**
 * The catalog narrowed to the one eCTD leaf a document IS — its skeleton
 * entries and the modules that land in it.
 *
 * Returns the catalog BY REFERENCE (not a copy) whenever the narrowing does not
 * apply, which is the load-bearing half: a doctype with no `documents:` block
 * (CSR, CER, PROTOCOL_DRUG and 11 others are legitimately one document), a
 * document with no stored `leaf_document_id` (every CMC document created before
 * the split), and a stored id the doctype does not declare (a stale record must
 * widen back to the whole doctype, never empty the draft view) all come back
 * completely unchanged. Reference identity also keeps callers' `useMemo`
 * results stable across renders.
 *
 * Module membership is resolved by `documentPicker.moduleDocumentMap` — the
 * existing mirror of `documents.py`'s `_module_document_claims` cascade (own id
 * first, then every declared section in order). Never `sections[0]` in
 * isolation: a module can declare an ancestor heading as its first section and
 * belong to a different leaf than that heading does.
 */
export function scopeCatalogToLeaf<C extends OutlineCatalog>(
  catalog: C | null | undefined,
  leafDocumentId: string | null | undefined,
): C | null {
  if (!catalog) return catalog ?? null;
  if (!leafDocumentId) return catalog;
  const current = (catalog.documents ?? []).find((d) => d.id === leafDocumentId);
  // A pre-split leaf id (CMC_BIOLOGIC `3.2.S` before 2026-10-08) still scopes
  // to its old section set; its modules are those whose current leaf it
  // fully contains (`documents.py::modules_of_document`, legacy branch).
  const legacy = current
    ? undefined
    : (catalog.documents?.length ? (catalog.legacy_documents ?? []) : []).find((d) => d.id === leafDocumentId);
  const leaf = current ?? legacy;
  if (!leaf) return catalog;

  const mine = new Set(leaf.sections);
  const moduleToDoc = moduleDocumentMap(catalog);
  const docSections = new Map((catalog.documents ?? []).map((d) => [d.id, d.sections]));
  const belongs = (docId: string | undefined): boolean =>
    current
      ? docId === leafDocumentId
      : docId !== undefined && docSections.has(docId) && (docSections.get(docId) ?? []).every((sec) => mine.has(sec));
  // Built as a mutable local and widened on return: the catalog TYPE is
  // readonly (both surfaces' own doctype types are), but constructing the
  // narrowed copy is exactly the one place a write is legitimate.
  const modules: Record<string, { readonly title?: string; readonly sections?: readonly string[] }> =
    {};
  for (const [mid, info] of Object.entries(catalog.modules ?? {})) {
    if (belongs(moduleToDoc.get(mid))) modules[mid] = info;
  }
  return {
    ...catalog,
    skeleton: catalog.skeleton?.filter(([number]) => mine.has(number)),
    modules,
    // This catalog is now one leaf, and says so — the narrowing having
    // APPLIED is a fact only this function knows (every non-applying branch
    // above returns the catalog by reference, unmarked).
    leafDocumentId,
  };
}
